// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import validator from "validator";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { PersonModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PersonModel";
import { PropertyDetailPersonDTO } from "Globals/Models/DTO/PropertyDetailPersonDTO";

// ViewModels
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";

export default class PersonViewModel extends ViewModelBase<PersonModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(personModel?: PersonModel) {
        super(personModel ? personModel : new PersonModel());
    }

    @action
    public stash = async (propertyDetails: PropertyDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            let model = new PersonModel();
            model.fromDto(this.model);
            let viewModel = new PersonViewModel(model);

            runInAction(() => {
                propertyDetails.personViewModels.push(viewModel);
                // triggers the auto-save.
                propertyDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (propertyDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.propertyDetailId = propertyDetailId;

            let dto: PropertyDetailPersonDTO = this.model.toDto();
            const apiResult = await this.Post<PropertyDetailPersonDTO>(AppUrls.Server.ApplicationForm.Upsert.Person, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("Person:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("Person:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (propertyDetails: PropertyDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("propertyDetailId")) {
                runInAction(() => {
                    let index = propertyDetails.personViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        propertyDetails.personViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.propertyDetailId = propertyDetails.model.id ? propertyDetails.model.id : "";
                this.model.isDeleted = true;

                let upsertPerson: PropertyDetailPersonDTO = this.model.toDto();
                const apiResult = await this.Post<PropertyDetailPersonDTO>(AppUrls.Server.ApplicationForm.Upsert.Person, upsertPerson);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("Person:Upsert-Successful");

                    let model = new PersonModel();
                    model.fromDto(apiResult.payload);
                    let viewModel = new PersonViewModel(model);
                    const filtered = propertyDetails.personViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        propertyDetails.personViewModels = filtered;
                    });
                    this.model.resetModel();
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("Person:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<PersonModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public setDate(fieldName: keyof FieldType<PersonModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
    }

    @computed
    public get getFormattedDateOfBirth(): string {
        return moment.utc(this.model.dateOfBirth).local().format("DD/MM/YYYY");
    }

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<PersonModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<PersonModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<PersonModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
